import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { appointmentService, doctorService, patientService } from '@/services/api';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  MoreHorizontal,
  XCircle,
  CheckCircle,
  Eye,
  Stethoscope,
  List,
  CalendarDays,
  CalendarRange,
  User,
  Filter,
  ArrowRight,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthorizedButton } from '@/components/common/AuthorizedButton';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, formatPhone, formatDoctorName, getDoctorInitials } from '@/lib/utils';
import {
  formatDate,
  formatTime,
  getKolkataTodayString,
  getKolkataWeekDays,
  kolkataToUtcIso,
  utcToKolkataParts,
} from '@/utils/dateTime';

// Normalized status color mapping
export const statusConfig = {
  BOOKED: { label: 'Booked', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
  CONFIRMED: { label: 'Confirmed', bg: 'bg-teal-50 text-teal-700 border-teal-200' },
  IN_PROGRESS: { label: 'In Progress', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
  COMPLETED: { label: 'Completed', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  CANCELLED: { label: 'Cancelled', bg: 'bg-rose-50 text-rose-700 border-rose-200' },
  NO_SHOW: { label: 'No Show', bg: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const AppointmentListPage = () => {
  const navigate = useNavigate();
  const { activeClinicId, buildUrl } = useClinic();
  const { user, hasRole, hasPermission } = useAuth();

  const canViewAllAppointments = hasPermission('APPOINTMENT_ALL_VIEW') || hasPermission('PLATFORM_MANAGE');

  const [viewMode, setViewMode] = useState('list'); // 'list' | 'day' | 'week'
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDateStr, setSelectedDateStr] = useState(getKolkataTodayString());
  const [selectedDoctor, setSelectedDoctor] = useState(canViewAllAppointments ? 'all' : (user?.id || 'all'));
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedClinic, setSelectedClinic] = useState(activeClinicId || 'all');

  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);

  useEffect(() => {
    loadDoctors();
    loadPatients();
  }, []);

  useEffect(() => {
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateStr, selectedDoctor, selectedStatus, selectedClinic, viewMode]);

  const loadDoctors = async () => {
    try {
      const response = await doctorService.getAll();
      setDoctors(response.data || []);
    } catch (error) {
      console.error('Failed to load doctors:', error);
    }
  };

  const loadPatients = async () => {
    try {
      const response = await patientService.getAll({ limit: 200 });
      setPatients(response.data || []);
    } catch (error) {
      console.error('Failed to load patients for appointments:', error);
    }
  };

  const weekDays = getKolkataWeekDays(selectedDateStr);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const params = {
        doctorId: selectedDoctor !== 'all' ? selectedDoctor : undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        clinicId: selectedClinic !== 'all' ? selectedClinic : undefined,
      };

      if (viewMode === 'week') {
        params.startDate = weekDays[0].dateString;
        params.endDate = weekDays[6].dateString;
      } else {
        params.date = selectedDateStr;
      }

      const response = await appointmentService.getAll(params);
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;
    try {
      await appointmentService.cancel(appointmentToCancel.id);
      toast.success('Appointment cancelled successfully');
      loadAppointments();
    } catch (error) {
      toast.error('Failed to cancel appointment');
    } finally {
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await appointmentService.updateStatus(appointmentId, newStatus);
      toast.success(`Appointment marked as ${newStatus.toLowerCase()}`);
      loadAppointments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDateNav = (direction) => {
    const shiftDays = viewMode === 'week' ? 7 : 1;
    const currentUtc = new Date(kolkataToUtcIso(selectedDateStr, '12:00:00')).getTime();
    const targetUtc = currentUtc + direction * shiftDays * 86400000;
    const parts = utcToKolkataParts(new Date(targetUtc));
    if (parts) {
      setSelectedDateStr(parts.dateString);
    }
  };

  // Lookup maps for fast and resilient enrichment
  const doctorsMap = useMemo(() => {
    const map = {};
    doctors.forEach((d) => {
      if (d.id) map[d.id] = d;
    });
    return map;
  }, [doctors]);

  const patientsMap = useMemo(() => {
    const map = {};
    patients.forEach((p) => {
      if (p.id) map[p.id] = p;
    });
    return map;
  }, [patients]);

  // Enrich appointments with loaded patient and doctor details
  const enrichedAppointments = useMemo(() => {
    return appointments.map((apt) => {
      const matchedPatient = patientsMap[apt.patientId] || {};
      const matchedDoctor = doctorsMap[apt.doctorId] || {};

      const patientName = (apt.patient?.name && apt.patient.name !== 'Unknown' && apt.patient.name !== 'Patient')
        ? apt.patient.name
        : (matchedPatient.name || `${matchedPatient.firstName || ''} ${matchedPatient.lastName || ''}`.trim() || apt.patient?.name || 'Patient');

      const pParts = patientName.split(' ').filter(Boolean);
      const patientFirstName = (apt.patient?.firstName && apt.patient.firstName !== 'Patient')
        ? apt.patient.firstName
        : (matchedPatient.firstName || pParts[0] || 'Patient');
      const patientLastName = apt.patient?.lastName
        ? apt.patient.lastName
        : (matchedPatient.lastName || (pParts.length > 1 ? pParts.slice(1).join(' ') : ''));

      const patientPhone = apt.patient?.phone || matchedPatient.phone || matchedPatient.contactNumber || '';
      const patientCode = apt.patient?.patientCode || matchedPatient.patientCode || '';

      const rawDocName = (apt.doctor?.name && apt.doctor.name !== 'Dr. Consultant' && apt.doctor.name !== 'Doctor')
        ? apt.doctor.name
        : (matchedDoctor.name || apt.doctor?.name || '');
      const doctorTarget = (apt.doctor?.firstName || matchedDoctor.firstName) ? (apt.doctor?.firstName ? apt.doctor : matchedDoctor) : (rawDocName || apt.doctor || matchedDoctor);
      const formattedDocName = formatDoctorName(doctorTarget, 'Dr. Consultant');
      const doctorSpec = matchedDoctor.specialization || apt.doctor?.specialization || 'General Medicine';

      return {
        ...apt,
        patient: {
          ...apt.patient,
          ...matchedPatient,
          name: patientName,
          firstName: patientFirstName,
          lastName: patientLastName,
          phone: patientPhone,
          patientCode: patientCode,
        },
        doctor: {
          ...apt.doctor,
          ...matchedDoctor,
          name: formattedDocName,
          firstName: doctorFirstName,
          lastName: doctorLastName,
          specialization: doctorSpec,
        },
      };
    });
  }, [appointments, patientsMap, doctorsMap]);

  // Filter local search on patient / doctor name / ID
  const filteredAppointments = enrichedAppointments.filter((apt) => {
    if (!search) return true;
    const s = search.toLowerCase();
    const patientMatch =
      apt.patient?.name?.toLowerCase().includes(s) ||
      apt.patient?.phone?.includes(s) ||
      apt.patient?.patientCode?.toLowerCase().includes(s);
    const doctorMatch =
      apt.doctor?.name?.toLowerCase().includes(s) ||
      apt.doctor?.specialization?.toLowerCase().includes(s);
    const idMatch = String(apt.id).toLowerCase().includes(s);
    return patientMatch || doctorMatch || idMatch;
  });

  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
  ];

  return (
    <div className="space-y-6" data-testid="appointment-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">
            {canViewAllAppointments ? 'Appointments Management' : 'My Appointments & Schedule'}
          </h1>
          <p className="text-slate-500 text-sm">
            Schedule, monitor, and manage clinical visits across hospital clinics (Asia/Kolkata IST)
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={cn('text-xs h-8 px-3', viewMode === 'list' && 'bg-white text-slate-900 shadow-sm hover:bg-white')}
            >
              <List className="h-3.5 w-3.5 mr-1.5" />
              List
            </Button>
            <Button
              variant={viewMode === 'day' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('day')}
              className={cn('text-xs h-8 px-3', viewMode === 'day' && 'bg-white text-slate-900 shadow-sm hover:bg-white')}
            >
              <CalendarDays className="h-3.5 w-3.5 mr-1.5" />
              Day
            </Button>
            <Button
              variant={viewMode === 'week' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('week')}
              className={cn('text-xs h-8 px-3', viewMode === 'week' && 'bg-white text-slate-900 shadow-sm hover:bg-white')}
            >
              <CalendarRange className="h-3.5 w-3.5 mr-1.5" />
              Week
            </Button>
          </div>

          {/* New Appointment Button */}
          <AuthorizedButton
            permission="APPOINTMENT_WRITE"
            tooltipMessage="You don't have access to schedule appointments"
            onClick={() => navigate(buildUrl('appointments/new'))}
            className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs shadow-sm h-10 px-4"
            data-testid="btn-new-appointment"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Book Appointment
          </AuthorizedButton>
        </div>
      </div>

      {/* Filter Toolbar */}
      <Card className="border-slate-200/80 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => handleDateNav(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 px-3 text-xs font-semibold flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-teal-600" />
                  {viewMode === 'week' ? (
                    <span>
                      {weekDays[0].fullDisplay} - {weekDays[6].fullDisplay}
                    </span>
                  ) : (
                    <span>{formatDate(selectedDateStr, 'FULL')}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={new Date(kolkataToUtcIso(selectedDateStr, '12:00:00'))}
                  onSelect={(date) => {
                    if (date) {
                      const p = utcToKolkataParts(date);
                      if (p) setSelectedDateStr(p.dateString);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => handleDateNav(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDateStr(getKolkataTodayString())}
              className="text-xs text-teal-700 hover:bg-teal-50"
            >
              Today
            </Button>
          </div>

          {/* Secondary Filters */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder="Search patient, phone, ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-9 text-xs"
              />
            </div>

            {/* Doctor Filter (Only visible if user has APPOINTMENT_ALL_VIEW permission) */}
            {canViewAllAppointments && (
              <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
                <SelectTrigger className="h-9 w-[180px] text-xs">
                  <SelectValue placeholder="All Doctors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {formatDoctorName(d)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="h-9 w-[150px] text-xs">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="BOOKED">Booked</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                <SelectItem value="NO_SHOW">No Show</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Main View: List OR Calendar */}
      {viewMode === 'list' ? (
        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="py-4 px-6 border-b border-slate-100 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-800">
              Appointments ({filteredAppointments.length})
            </CardTitle>
            <Badge variant="outline" className="text-xs font-normal text-slate-500 font-mono">
              Date: {selectedDateStr} (IST)
            </Badge>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : filteredAppointments.length === 0 ? (
              <div className="p-12 text-center">
                <CalendarIcon className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600 font-medium">No appointments match the selected filters</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(buildUrl('appointments/new'))}
                  className="mt-4 text-xs"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Schedule First Appointment
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-slate-50/50">
                    <TableHead className="w-24">Time (IST)</TableHead>
                    <TableHead>Patient Details</TableHead>
                    <TableHead>Consulting Doctor</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAppointments.map((apt) => {
                    const cfg = statusConfig[apt.status?.toUpperCase()] || statusConfig.BOOKED;
                    const patientDisplay = apt.patient?.name || `${apt.patient?.firstName || ''} ${apt.patient?.lastName || ''}`.trim() || 'Patient';
                    const initials = (apt.patient?.firstName?.[0] || apt.patient?.name?.[0] || 'P') + (apt.patient?.lastName?.[0] || '');
                    const phoneDisplay = apt.patient?.phone && apt.patient.phone !== '-' ? formatPhone(apt.patient.phone) : '';
                    const doctorDisplay = formatDoctorName(apt.doctor);
                    const typeDisplay = String(apt.type || apt.appointmentType || apt.reason || 'General Consultation').replace(/[-_]/g, ' ');

                    return (
                      <TableRow
                        key={apt.id}
                        className="hover:bg-slate-50/60 cursor-pointer text-xs"
                        onClick={() => navigate(buildUrl(`appointments/${apt.id}`))}
                      >
                        <TableCell className="font-semibold text-slate-900 font-mono">
                          {apt.timeFormatted || apt.time}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-7 w-7">
                              <AvatarFallback className="bg-teal-100 text-teal-700 text-xs font-semibold">
                                {initials}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-semibold text-slate-900 leading-tight">
                                {patientDisplay}
                              </p>
                              {(apt.patient?.patientCode || phoneDisplay) ? (
                                <p className="text-[11px] text-slate-400 font-mono">
                                  {apt.patient?.patientCode ? `${apt.patient.patientCode}` : ''}
                                  {apt.patient?.patientCode && phoneDisplay ? ' • ' : ''}
                                  {phoneDisplay}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium text-slate-800">
                            {doctorDisplay}
                          </p>
                          <p className="text-[11px] text-slate-400">{apt.doctor?.specialization || 'General Medicine'}</p>
                        </TableCell>
                        <TableCell className="capitalize text-slate-600">{typeDisplay}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn('text-[11px] font-semibold uppercase tracking-wider', cfg.bg)}>
                            {cfg.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {(apt.status === 'CONFIRMED' || apt.status === 'IN_PROGRESS') && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                                onClick={() => navigate(buildUrl(`consultations/${apt.id}?appointmentId=${apt.id}&patientId=${apt.patientId}`))}
                              >
                                <Stethoscope className="h-3 w-3 mr-1" />
                                Consult
                              </Button>
                            )}

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="text-xs">
                                <DropdownMenuItem onClick={() => navigate(buildUrl(`appointments/${apt.id}`))}>
                                  <Eye className="h-3.5 w-3.5 mr-2" /> View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(buildUrl(`patients/${apt.patientId}`))}>
                                  <User className="h-3.5 w-3.5 mr-2" /> View Patient Record
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {apt.status !== 'COMPLETED' && apt.status !== 'CANCELLED' && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleStatusChange(apt.id, 'CONFIRMED')}>
                                      <CheckCircle className="h-4 w-4 mr-2 text-teal-600" /> Mark Confirmed
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleStatusChange(apt.id, 'NO_SHOW')}>
                                      <RotateCcw className="h-4 w-4 mr-2 text-amber-600" /> Mark No-Show
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="text-rose-600"
                                      onClick={() => {
                                        setAppointmentToCancel(apt);
                                        setCancelDialogOpen(true);
                                      }}
                                    >
                                      <XCircle className="h-4 w-4 mr-2" /> Cancel Appointment
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'day' ? (
        /* Day Calendar View */
        <Card className="border-slate-200/80 shadow-sm overflow-hidden">
          <CardHeader className="py-3 px-6 border-b border-slate-100 bg-slate-50/50 flex flex-row items-center justify-between">
            <span className="font-semibold text-sm text-slate-800">
              Schedule for {formatDate(selectedDateStr, 'FULL')}
            </span>
            <span className="text-xs text-slate-500">
              {filteredAppointments.length} Booked Slots
            </span>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2">
              {timeSlots.map((slot) => {
                const slotAppointments = filteredAppointments.filter(a => a.time === slot);
                return (
                  <div key={slot} className="flex items-start gap-4 p-2 rounded-lg hover:bg-slate-50 border-b border-slate-100/80 min-h-[56px]">
                    <div className="w-16 font-mono text-xs text-slate-500 font-semibold pt-1">
                      {slot}
                    </div>
                    <div className="flex-1 flex flex-wrap gap-2">
                      {slotAppointments.length === 0 ? (
                        <span className="text-xs text-slate-300 italic pt-1">Available slot</span>
                      ) : (
                        slotAppointments.map((apt) => {
                          const statusInfo = statusConfig[apt.status?.toUpperCase()] || statusConfig.BOOKED;
                          return (
                            <div
                              key={apt.id}
                              onClick={() => navigate(buildUrl(`appointments/${apt.id}`))}
                              className={cn(
                                'flex items-center gap-3 px-3 py-1.5 rounded-lg border text-xs cursor-pointer hover:shadow-md transition-shadow',
                                statusInfo.bg
                              )}
                            >
                              <span className="font-semibold">{apt.patient?.name || `${apt.patient?.firstName || ''} ${apt.patient?.lastName || ''}`.trim() || 'Patient'}</span>
                              <span className="opacity-75">• {formatDoctorName(apt.doctor)}</span>
                              <Badge variant="outline" className="text-[10px] uppercase font-bold tracking-wider py-0 px-1.5 bg-white/60">
                                {String(apt.type || apt.appointmentType || apt.reason || 'General').replace(/[-_]/g, ' ')}
                              </Badge>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Week Calendar Grid View */
        <Card className="border-slate-200/80 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[750px] grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-center text-xs font-semibold py-3 text-slate-700">
              {weekDays.map((day) => {
                const isSelected = day.dateString === selectedDateStr;
                return (
                  <div
                    key={day.dateString}
                    onClick={() => setSelectedDateStr(day.dateString)}
                    className={cn(
                      'cursor-pointer py-1 rounded-md mx-1 transition-colors',
                      isSelected ? 'bg-teal-600 text-white font-bold' : day.isToday ? 'text-teal-700 font-bold' : 'hover:bg-slate-200/60'
                    )}
                  >
                    <p>{day.dayName}</p>
                    <p className="text-sm mt-0.5">{day.dayNumber}</p>
                  </div>
                );
              })}
            </div>

            <div className="min-w-[750px] grid grid-cols-7 divide-x divide-slate-100 p-2 min-h-[380px]">
              {weekDays.map((day) => {
                const dayApts = enrichedAppointments.filter(a => a.date === day.dateString);
                return (
                  <div key={day.dateString} className="p-1 space-y-2">
                    {dayApts.length === 0 ? (
                      <p className="text-[11px] text-slate-300 text-center py-4">No visits</p>
                    ) : (
                      dayApts.map(apt => {
                        const statusInfo = statusConfig[apt.status?.toUpperCase()] || statusConfig.BOOKED;
                        return (
                          <div
                            key={apt.id}
                            onClick={() => navigate(buildUrl(`appointments/${apt.id}`))}
                            className={cn('p-2 rounded-lg border text-[11px] cursor-pointer hover:shadow transition-shadow', statusInfo.bg)}
                          >
                            <p className="font-bold truncate">{apt.timeFormatted || apt.time} • {apt.patient?.name || apt.patient?.firstName || 'Patient'}</p>
                            <p className="opacity-80 truncate">{formatDoctorName(apt.doctor)}</p>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* Cancel Appointment Alert Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the appointment for{' '}
              <strong>
                {appointmentToCancel?.patient?.name || `${appointmentToCancel?.patient?.firstName || ''} ${appointmentToCancel?.patient?.lastName || ''}`.trim() || 'Patient'}
              </strong>{' '}
              on {appointmentToCancel?.formattedDateTime || appointmentToCancel?.date}? This will notify the patient and free up the doctor's slot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelAppointment}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Cancel Appointment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AppointmentListPage;
